import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RolesData } from '../../../../@core/interfaces/common/roles';
import { environment } from '../../../../../environments/environment';
import { ToastrAppSrvcService } from '../../../../_services/toastr-app-srvc.service';

@Component({
  selector: 'ngx-symptom-checker',
  templateUrl: './symptom-checker.component.html',
  styleUrls: ['./symptom-checker.component.scss']
})
export class SymptomCheckerComponent implements OnInit {
  @Input() fromPage: string;
  @Output() backEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() completeEvent: EventEmitter<any> = new EventEmitter<any>();
  state: 'intake' | 'questions' | 'results' = 'intake';
  genderList: Array<any> = ['Male', 'Female', 'Other / Prefer not to say'];
  // Forms
  intakeForm: FormGroup;
  questionsForm: FormGroup;
  regId=localStorage.getItem('regID');
  userId=localStorage.getItem('userId');
  // Data
  sessionId: string | null = null;
  sessionNumber: string | null = null;
  questions: Array<any> = [];
  quickResult: {} = null;


  // Skipped questions tracking
  skippedQuestions: Set<string> = new Set();


  // Loading state for the "start triage" API call
  isLoading = false;

  // ─── Chief Complaint: predefined common complaints ───────────────────────────
  commonComplaints: Array<any> = [];

  // ─── Duration: predefined quick-select options ───────────────────────────────
  durationOptions: string[] = [
    'Less than 1 hour',
    '1–6 hours',
    '6–12 hours',
    '12–24 hours',
    '1–3 days',
    '3–7 days',
    'More than 1 week',
    'More than 1 month',
  ];

  protected readonly unsubscribe$ = new Subject<void>();
  private tenantId = localStorage.getItem("tenantId");
  private branchId = localStorage.getItem("branchId");
  get fval(): { [key: string]: AbstractControl } { return this.intakeForm.controls; }
  isSubmitted: boolean = false;
  constructor(private toaster: ToastrAppSrvcService, private rolesData: RolesData, private fb: FormBuilder
  ) {
    this.intakeForm = this.fb.group({
      age: this.fb.control(null, [Validators.required, Validators.pattern(/^[1-9][0-9]*$/)]),
      sex: this.fb.control('', [Validators.required]),
      chiefComplaint: this.fb.control('', [Validators.required]),
      duration: this.fb.control('', [Validators.required])
    });
  }

  ngOnInit(): void {
    this.loadChiefComplaints();
  }

  loadChiefComplaints(): void {
    this.rolesData.list("chiefcomplaint/" + this.tenantId + '/' + this.branchId).pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => {
      this.commonComplaints = data;
    }, (error) => {
    }, () => {
    });
  }

  selectComplaint(complaint: string): void {
    const control = this.intakeForm.get('chiefComplaint');
    const currentValue = control?.value || '';

    let complaints = currentValue.split(',').map(x => x.trim()).filter(x => x);

    const isExists = complaints.some(x => x?.trim()?.toLowerCase() === complaint?.trim()?.toLowerCase());

    if (isExists) {
      complaints = complaints.filter(x => x !== complaint);
    } else {
      complaints.push(complaint);
    }
    control?.setValue(complaints.join(', '));
  }

  selectDuration(duration: string): void {
    this.intakeForm.get('duration').setValue(duration);
    this.intakeForm.get('duration').markAsTouched();
  }

  trackByQuestionId(index: number, question): string {
    return question.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  skipQuestion(questionId: string): void {
    this.skippedQuestions.add(questionId);
    const ctrl = this.questionsForm.get(questionId);
    if (ctrl) {
      ctrl.reset();
      ctrl.clearValidators();
      ctrl.updateValueAndValidity();
    }
  }

  undoSkip(questionId: string): void {
    this.skippedQuestions.delete(questionId);
    const ctrl = this.questionsForm.get(questionId);
    if (ctrl) { ctrl.enable(); }
  }

  isSkipped(questionId: string): boolean {
    return this.skippedQuestions.has(questionId);
  }

  startTriage(event): void {
    event.target.disabled = true;
    this.isSubmitted = true;

    if (this.fromPage == 'CareSphere') {
      this.intakeForm.get('age').setValue(localStorage.getItem('age'));
      this.intakeForm.get('sex').setValue(localStorage.getItem('gender'));
    }

    if (this.intakeForm.valid) {
      const postData = this.intakeForm?.getRawValue();
      postData.age = Number(postData.age);

      this.isLoading = true;

      this.rolesData.create(`${environment.symptomCheckerApiUrl}/triage/start`, postData).pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe(
        (res) => {
          this.sessionId = res.sessionId;
          this.sessionNumber = res.sessionNumber;
          this.questions = res.questions || [];
          this.skippedQuestions.clear();
          this.initQuestionsForm();
          this.state = 'questions';
          event.target.disabled = false; this.isSubmitted = false;
          this.toaster.ForSuccess('Symptom checker started successfully.');
          this.isLoading = false;
        },
        (error) => {
          event.target.disabled = false;
          this.isLoading = false;
          this.toaster.ForDanger('Failed to start symptom checker. Please try again.');
        },
        () => {
        }
      )
    }
    else {
      event.target.disabled = false;
      this.toaster.ForInfo('Please fill all required fields.');
    }
  }

  private initQuestionsForm(): void {
    const group: { [key: string]: any } = {};
    this.questions.forEach(q => {
      if (q.type === 'multi_choice') { group[q.id] = [[]]; }
      else if (q.type === 'severity_scale') { group[q.id] = [5]; }
      else { group[q.id] = [null]; }
    });
    this.questionsForm = this.fb.group(group);
  }

  onMultiChoiceChange(questionId: string, option: string, isChecked: boolean): void {
    const control = this.questionsForm.get(questionId);
    if (control) {
      const current: string[] = control.value || [];
      control.setValue(isChecked ? [...current, option] : current.filter(v => v !== option));
      control.markAsDirty();
      control.updateValueAndValidity();
    }
  }

  submitAnswers(event): void {
   
    event.target.disabled = true;

    const answers = this.questions
      .filter(q => !this.skippedQuestions.has(q.id))
      .map(q => {
        let rawVal = this.questionsForm.value[q.id];
        if (rawVal === null || rawVal === undefined || rawVal === '' ||
          (Array.isArray(rawVal) && rawVal.length === 0)) {
          return null;
        }
        if (q.type === 'boolean') {
          rawVal = rawVal === 'true' || rawVal === true ? 'Yes' : 'No';
        }
        return { questionId: q.id, answer: rawVal } as {};
      })
      .filter(a => a !== null) as [];

      if (!answers.length) {
        event.target.disabled = false;
        this.toaster.ForInfo('Please fill at least one field before saving.');
        return;
      }
this.isLoading = true;
    const payload = { sessionId: this.sessionId!, answers };
  
    this.rolesData.create(`${environment.symptomCheckerApiUrl}/triage/specialist-recommendation`, payload).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (res) => {
        this.quickResult = res;
        this.fetchDetailedResult(payload, event);
      },
      (error) => {
        this.fetchDetailedResult(payload, event);
        this.toaster.ForDanger('Failed to start symptom checker. Please try again.');
      },
      () => {
      }
    )

  }

  // ── POST /api/triage/complete/detailed ──────────────────────────────────────
  private fetchDetailedResult(payload: any, event: any): void {


    this.rolesData.create(`${environment.symptomCheckerApiUrl}/triage/clinical-assessment`, payload).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (res) => {
        this.completeEvent.emit(res);
        this.isLoading = false;
        event.target.disabled = false;
      },
      (error) => {
        this.isLoading = false;
        event.target.disabled = false;
      },
      () => {
      }
    )

  }

  reset(): void {
    this.state = 'intake';
    this.sessionId = null;
    this.sessionNumber = null;
    this.questions = [];
    this.quickResult = null;
    this.skippedQuestions.clear();
    this.intakeForm.reset(
      {
        severity: 5
      }
    );
    this.isSubmitted = false;
  }

  isComplaintSelected(complaint: string): boolean {
    const value = this.intakeForm.get('chiefComplaint')?.value || '';

    return value
      .split(',')
      .map(x => x.trim().toLowerCase())
      .includes(complaint?.trim().toLowerCase());
  }

  backToCareSphere() {
    this.backEvent.emit();
  }
}
